// Function index:
//   DoorLineHandle             — one SVG <line> binding (logical edge +
//                                 endpoint ids + locked flag).
//   buildScene(parent, ext)    — root scroll container + inner sized box
//                                 + SVG door layer.
//   buildDoorLines(svg, doors, roomById) — one SVG <line> per logical door
//                                 edge; visibility toggles in refresh().
//   buildRoomCards(args)       — absolute-positioned div per room, click
//                                 routed via the `reachable` class.
//
// Pure DOM construction — no fog logic here. refresh.ts mutates the
// returned handles based on the player's position.

import { PAD, CELL_W, CELL_H, ROOM_W, ROOM_H, roomCenter } from './layout.js';
import type { BootstrapDoor, BootstrapRoom } from '../api/types.js';

export interface DoorLineHandle {
  line: SVGLineElement;
  fromId: number;
  toId: number;
  locked: boolean;
}

export interface SceneNodes {
  root: HTMLDivElement;
  inner: HTMLDivElement;
  svg: SVGSVGElement;
}

export function buildScene(parent: HTMLElement, ext: { w: number; h: number }): SceneNodes {
  const root = document.createElement('div');
  root.className = 'rz-room-scene';
  root.style.cssText = 'position: absolute; inset: 0; overflow: auto; background: #0a0a0a;';
  parent.appendChild(root);

  const inner = document.createElement('div');
  inner.style.cssText = `position: relative; width: ${ext.w}px; height: ${ext.h}px;`;
  root.appendChild(inner);

  // SVG layer for door edges. Sized to the inner container so lines stay
  // pinned to absolute coords; sits behind the room cards.
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(ext.w));
  svg.setAttribute('height', String(ext.h));
  svg.style.cssText = 'position: absolute; inset: 0; pointer-events: none;';
  inner.appendChild(svg);

  return { root, inner, svg };
}

export function buildDoorLines(
  svg: SVGSVGElement,
  doors: BootstrapDoor[],
  roomById: Map<number, BootstrapRoom>,
): DoorLineHandle[] {
  const handles: DoorLineHandle[] = [];
  const drawn = new Set<string>();
  for (const d of doors) {
    const a = Math.min(d.from_room_id, d.to_room_id);
    const b = Math.max(d.from_room_id, d.to_room_id);
    const key = `${a}-${b}`;
    if (drawn.has(key)) continue;
    drawn.add(key);
    const from = roomById.get(d.from_room_id);
    const to = roomById.get(d.to_room_id);
    if (!from || !to) continue;
    const fc = roomCenter(from);
    const tc = roomCenter(to);
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', String(fc.x));
    line.setAttribute('y1', String(fc.y));
    line.setAttribute('x2', String(tc.x));
    line.setAttribute('y2', String(tc.y));
    line.setAttribute('stroke', d.locked ? '#883a3a' : '#444');
    line.setAttribute('stroke-width', '2');
    line.setAttribute('stroke-dasharray', d.locked ? '6 4' : '');
    svg.appendChild(line);
    handles.push({ line, fromId: a, toId: b, locked: d.locked });
  }
  return handles;
}

export interface BuildRoomCardsArgs {
  inner: HTMLDivElement;
  rooms: BootstrapRoom[];
  onClick: (room: BootstrapRoom) => void;
}

export function buildRoomCards(args: BuildRoomCardsArgs): Map<number, HTMLDivElement> {
  const { inner, rooms, onClick } = args;
  const map = new Map<number, HTMLDivElement>();
  for (const r of rooms) {
    const el = document.createElement('div');
    el.className = 'rz-room';
    el.style.cssText = `position: absolute; left: ${PAD + r.x * CELL_W}px; top: ${PAD + r.y * CELL_H}px; width: ${ROOM_W}px; height: ${ROOM_H}px; background: #141414; border: 1px solid #2a2a2a; border-radius: 6px; padding: 8px 10px; box-sizing: border-box; color: #ddd; font-size: 11px; cursor: default; transition: border-color 120ms ease, background 120ms ease, opacity 200ms ease;`;
    el.dataset.q = String(r.x);
    el.dataset.r = String(r.y);
    el.addEventListener('click', () => {
      if (!el.classList.contains('reachable')) return;
      onClick(r);
    });
    inner.appendChild(el);
    map.set(r.id, el);
  }
  return map;
}
